import { compareISODate, parseISODate, type ISODate } from "../domain";
import { mean, median } from "./math";

export interface ForwardReversionPoint {
  readonly date: ISODate;
  readonly episodeId: string;
  readonly metricValue: number;
  readonly seasonalZ: number;
  readonly eligible?: boolean;
  readonly adfPass?: boolean;
  readonly cointegrationPass?: boolean;
  readonly halfLife?: number;
  readonly returnCorrelation?: number;
  readonly curveContributionMagnitude?: number;
  readonly contractYear?: number;
  readonly pairId?: string;
}

export interface ForwardReversionEvent {
  readonly date: ISODate;
  readonly episodeId: string;
  readonly side: "leg-a-rich" | "leg-a-cheap";
  readonly threshold: number;
  readonly horizon: number;
  readonly entryZ: number;
  readonly endpointZ: number;
  readonly rawChange: number;
  readonly reversionSignedChange: number;
  readonly revertedTowardZero: boolean;
  readonly winAfterCost: boolean;
  readonly mae: number;
  readonly mfe: number;
}

export interface ForwardReversionSummary {
  readonly side: "leg-a-rich" | "leg-a-cheap";
  readonly threshold: number;
  readonly horizon: number;
  readonly events: number;
  readonly meanChange: number | null;
  readonly medianChange: number | null;
  readonly reversionRate: number | null;
  readonly winRate: number | null;
  readonly mae: number | null;
  readonly mfe: number | null;
}

export interface ForwardReversionStudy {
  readonly scope: "actual-episode-forward-reversion";
  readonly events: readonly ForwardReversionEvent[];
  readonly summaries: readonly ForwardReversionSummary[];
  readonly horizons: readonly number[];
  readonly thresholds: readonly number[];
  readonly boundaryRule: "never-cross-actual-episode";
}

export interface ForwardReversionOptions {
  readonly thresholds?: readonly number[];
  readonly horizons?: readonly number[];
  readonly roundTripCostInMetricUnits?: number;
  readonly filter?: (point: ForwardReversionPoint) => boolean;
}

function thresholdReached(
  point: ForwardReversionPoint,
  previous: ForwardReversionPoint | undefined,
  threshold: number,
  side: ForwardReversionEvent["side"],
): boolean {
  if (side === "leg-a-rich") {
    return point.seasonalZ > threshold &&
      (previous === undefined || previous.episodeId !== point.episodeId || previous.seasonalZ <= threshold);
  }
  return point.seasonalZ < -threshold &&
    (previous === undefined || previous.episodeId !== point.episodeId || previous.seasonalZ >= -threshold);
}

/**
 * Expects observations from provenance-validated actual-contract episodes;
 * this numeric study cannot detect a back-adjusted source from point arrays.
 */
export function studyForwardReversion(
  points: readonly ForwardReversionPoint[],
  options: ForwardReversionOptions = {},
): ForwardReversionStudy {
  const thresholds = options.thresholds ?? [1.5, 2, 2.5];
  const horizons = options.horizons ?? [5, 10, 20, 40];
  if (thresholds.some((value) => !Number.isFinite(value) || value <= 0) || horizons.some((value) => !Number.isInteger(value) || value <= 0)) {
    throw new Error("Thresholds and horizons must be positive.");
  }
  const cost = options.roundTripCostInMetricUnits ?? 0;
  const byEpisode = new Map<string, ForwardReversionPoint[]>();
  const previousDateByEpisode = new Map<string, ISODate>();
  for (const point of points) {
    parseISODate(point.date);
    const previousDate = previousDateByEpisode.get(point.episodeId);
    if (previousDate !== undefined && compareISODate(previousDate, point.date) >= 0) {
      throw new Error(`Forward-reversion episode ${point.episodeId} must be strictly date-ordered.`);
    }
    previousDateByEpisode.set(point.episodeId, point.date);
    if (point.eligible === false || !Number.isFinite(point.metricValue) || !Number.isFinite(point.seasonalZ)) continue;
    const episode = byEpisode.get(point.episodeId) ?? [];
    episode.push(point);
    byEpisode.set(point.episodeId, episode);
  }

  const events: ForwardReversionEvent[] = [];
  for (const episode of byEpisode.values()) {
    for (let index = 0; index < episode.length; index += 1) {
      const point = episode[index];
      const previous = episode[index - 1];
      if (!point) continue;
      if (options.filter && !options.filter(point)) continue;
      for (const threshold of thresholds) {
      for (const side of ["leg-a-rich", "leg-a-cheap"] as const) {
          if (!thresholdReached(point, previous, threshold, side)) continue;
        const reversionDirection = side === "leg-a-rich" ? -1 : 1;
          for (const horizon of horizons) {
            const endpoint = episode[index + horizon];
            if (!endpoint) continue;
            const path = episode.slice(index + 1, index + horizon + 1);
            const signedChanges = path.map(
              (candidate) => reversionDirection * (candidate.metricValue - point.metricValue),
            );
            const rawChange = endpoint.metricValue - point.metricValue;
            const reversionSignedChange = reversionDirection * rawChange;
            events.push({
              date: point.date,
              episodeId: point.episodeId,
              side,
              threshold,
              horizon,
              entryZ: point.seasonalZ,
              endpointZ: endpoint.seasonalZ,
              rawChange,
              reversionSignedChange,
              revertedTowardZero: Math.abs(endpoint.seasonalZ) < Math.abs(point.seasonalZ),
              winAfterCost: reversionSignedChange > cost,
              mae: signedChanges.length > 0 ? Math.min(...signedChanges) : 0,
              mfe: signedChanges.length > 0 ? Math.max(...signedChanges) : 0,
            });
          }
        }
      }
    }
  }

  const summaries: ForwardReversionSummary[] = [];
  for (const threshold of thresholds) {
      for (const side of ["leg-a-rich", "leg-a-cheap"] as const) {
      for (const horizon of horizons) {
        const selected = events.filter(
          (event) => event.threshold === threshold && event.side === side && event.horizon === horizon,
        );
        summaries.push({
          side,
          threshold,
          horizon,
          events: selected.length,
          meanChange: selected.length > 0 ? mean(selected.map((event) => event.rawChange)) : null,
          medianChange: selected.length > 0 ? median(selected.map((event) => event.rawChange)) : null,
          reversionRate: selected.length > 0
            ? selected.filter((event) => event.revertedTowardZero).length / selected.length
            : null,
          winRate: selected.length > 0
            ? selected.filter((event) => event.winAfterCost).length / selected.length
            : null,
          mae: selected.length > 0 ? mean(selected.map((event) => event.mae)) : null,
          mfe: selected.length > 0 ? mean(selected.map((event) => event.mfe)) : null,
        });
      }
    }
  }

  return {
    scope: "actual-episode-forward-reversion",
    events,
    summaries,
    horizons,
    thresholds,
    boundaryRule: "never-cross-actual-episode",
  };
}
